/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { openTerminal, terminalSocketUrl } from "@/api/terminal";

type Phase = "confirm" | "connecting" | "live" | "ended";

/** Confirmation first (a console is a shell with full rights), then the terminal. */
export const TerminalModal: React.FC<{ kind: string; id: string; name: string; onClose: () => void }> = ({ kind, id, name, onClose }) => {
  const [phase, setPhase] = useState<Phase>("confirm");
  // the effect must start once per console, not again when the phase text changes
  const [started, setStarted] = useState(false);
  const [note, setNote] = useState("");
  const host = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!started) return;
    let cancelled = false;
    let term: Terminal | null = null;
    let ro: ResizeObserver | null = null;
    (async () => {
      let ticket: string;
      try {
        ticket = await openTerminal(kind, id);
      } catch (e) {
        if (!cancelled) {
          setNote((e as Error).message);
          setPhase("ended");
        }
        return;
      }
      if (cancelled || !host.current) return;
      term = new Terminal({
        cursorBlink: true,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
        fontSize: 13,
        theme: { background: "#0e0d0b", foreground: "#e9e3d6", cursor: "#7fb893" },
      });
      const fit = new FitAddon();
      term.loadAddon(fit);
      term.open(host.current);
      fit.fit();
      const ws = new WebSocket(terminalSocketUrl(ticket));
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;
      const enc = new TextEncoder();
      const sendSize = () => {
        if (ws.readyState === WebSocket.OPEN && term) ws.send(JSON.stringify({ type: "resize", rows: term.rows, cols: term.cols }));
      };
      ws.onopen = () => {
        setPhase("live");
        sendSize();
        term?.focus();
      };
      ws.onmessage = (ev) => term?.write(typeof ev.data === "string" ? ev.data : new Uint8Array(ev.data as ArrayBuffer));
      ws.onclose = (ev) => {
        setNote(ev.reason || "Session closed");
        setPhase("ended");
      };
      term.onData((d) => ws.readyState === WebSocket.OPEN && ws.send(enc.encode(d)));
      ro = new ResizeObserver(() => {
        fit.fit();
        sendSize();
      });
      ro.observe(host.current);
    })();
    return () => {
      cancelled = true;
      ro?.disconnect();
      wsRef.current?.close();
      term?.dispose();
    };
  }, [started, kind, id]);

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-3 sm:p-6" role="dialog" aria-modal="true" aria-label={`Console on ${name}`}>
      {phase === "confirm" ? (
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#171512] p-5 text-[13px] text-gray-200 shadow-2xl">
          <h3 className="text-[15px] font-semibold text-white">{`Open a console on ${name}?`}</h3>
          <p className="mt-2 text-gray-400">This is a shell with full rights on the device. The session is logged and closes by itself after 10 minutes without activity.</p>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className="gh-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="gh-btn gh-btn--primary" onClick={() => {
                setPhase("connecting");
                setStarted(true);
              }}
              autoFocus>
              Open console
            </button>
          </div>
        </div>
      ) : (
        <div className="flex h-[min(78vh,720px)] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0e0d0b] shadow-2xl">
          <div className="flex items-center gap-3 border-b border-white/10 px-4 py-2 text-[12.5px] text-gray-300">
            <span className={`h-2 w-2 rounded-full ${phase === "live" ? "bg-emerald-500" : phase === "ended" ? "bg-red-500" : "bg-amber-500"}`} />
            <b className="font-semibold text-white">{name}</b>
            <span className="text-gray-500">{phase === "connecting" ? "connecting…" : phase === "live" ? "connected" : note || "closed"}</span>
            <button type="button" className="gh-btn ml-auto" onClick={onClose}>
              {phase === "ended" ? "Close" : "End session"}
            </button>
          </div>
          <div ref={host} className="min-h-0 flex-1 p-2" />
        </div>
      )}
    </div>,
    document.body,
  );
};
